/**
 * Onboarding Wrapper Component
 * Automatically shows tutorial for first-time users
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { OnboardingTutorial } from './OnboardingTutorial';
import { useAuth } from '../lib/auth-context';
import { CHECKINS_UPDATED_EVENT, CLOUD_BOOTSTRAP_DONE_KEY } from '../lib/cloud-sync';
import { isOnboardingComplete, markOnboardingComplete } from '../lib/onboarding-state';

interface OnboardingWrapperProps {
  children: React.ReactNode;
}

// Temporary consumer kill-switch: disable login tutorial until refreshed content/logic ships.
const LOGIN_TUTORIAL_ENABLED = false;

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore restricted storage environments.
  }
}

export function OnboardingWrapper({ children }: OnboardingWrapperProps) {
  const [showTutorial, setShowTutorial] = useState(false);
  const [syncBootstrapReady, setSyncBootstrapReady] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setSyncBootstrapReady(true);
      return;
    }

    setSyncBootstrapReady(false);
    const startedAt = Date.now();
    let cancelled = false;

    const pollBootstrap = () => {
      if (cancelled) return;
      const done = safeLocalStorageGet(CLOUD_BOOTSTRAP_DONE_KEY) === 'true';
      const timedOut = Date.now() - startedAt > 5000;
      if (done || timedOut) {
        setSyncBootstrapReady(true);
        return;
      }
      window.setTimeout(pollBootstrap, 150);
    };

    pollBootstrap();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || !syncBootstrapReady) return;
    if (!LOGIN_TUTORIAL_ENABLED) {
      setShowTutorial(false);
      return;
    }

    // Check if user has completed or skipped onboarding
    const hasCompletedOnboarding = isOnboardingComplete();
    const hasSkippedOnboarding = safeLocalStorageGet('onboarding_skipped');
    const hasCompletedModeSelection = safeLocalStorageGet('tension_mode');
    
    // Show tutorial for authenticated users who:
    // 1. Haven't completed onboarding
    // 2. Haven't skipped onboarding
    // 3. Haven't selected a mode yet (new users)
    if (user && !hasCompletedOnboarding && !hasSkippedOnboarding && !hasCompletedModeSelection) {
      // Delay slightly to ensure page has loaded
      setTimeout(() => {
        setShowTutorial(true);
      }, 500);
    }
  }, [user, syncBootstrapReady]);

  const handleComplete = () => {
    markOnboardingComplete();
    setShowTutorial(false);
    window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
    // Navigate to mode selection if not already done
    const existingMode = safeLocalStorageGet('tension_mode');
    if (!existingMode) {
      navigate('/mode-selection');
    }
  };

  const handleSkip = () => {
    safeLocalStorageSet('onboarding_skipped', 'true');
    setShowTutorial(false);
    window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
    // Navigate to mode selection if not already done
    const existingMode = safeLocalStorageGet('tension_mode');
    if (!existingMode) {
      navigate('/mode-selection');
    }
  };

  return (
    <>
      {children}
      {showTutorial && (
        <OnboardingTutorial
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      )}
    </>
  );
}